import { Component, Inject } from "vue-property-decorator";
import Vue from "vue";
import AddFileModal from "@/modules/Profile/components/AddFileModal/AddFileModal.vue";
import { Categories, ListCategory } from "@/shared/models/ListCategory";
import { Http } from "@status/codes";
import { ListService } from "@/modules/List/services/ListService";
import { DocumentSaveDTO } from "@/modules/Document/models/DocumentSaveDTO";
import firebase from "firebase/app";
import "firebase/storage";
import { DocumentService } from "@/modules/Document/services/DocumentService";
import { FILE } from "@/shared/constants/FileConstants";
import { DocumentUpdateDTO } from "@/modules/Document/models/DocumentUpdateDTO";
import { Response } from "@/shared/models/Response";
import { PostMessageIntelliticks } from "../GroupTypeService/models/PostMessageIntelliticks";
import {
  EVENT_LISTENER,
  LOAD_DOCUMENT_CONSTANT
} from "./constants/LoadDocumentConstants";
import { CHAT_BOT_CONSTANTS } from "@/shared/components/ChatBot/constants/ChatBotConstants";
import { SessionService } from "@/modules/Sessions/services/SessionService";
import { SessionIntelliticks } from "../GroupTypeService/models/SessionIntelliticks";
import { Utils } from "@/shared/utils/utils";
import { ListQuery } from "@/modules/List/models/ListQuery";
import { List } from "@/modules/List/models/List";
import { Option } from "@/modules/List/models/Option";
import { LIST_QUERY } from "@/modules/Profile/constants/ProfileConstants";
import { LOCAL_STORAGE } from "@/shared/constants/FormConstants";

@Component({
  name: "LoadDocument",
  components: {
    AddFileModal
  }
})
export default class LoadDocument extends Vue {
  @Inject() listService!: ListService;
  @Inject() documentService!: DocumentService;
  @Inject() sessionService!: SessionService;

  categories: Categories[] = [];
  userId = "";
  msgIdView = "";
  showComponent = false;
  subCategoryDefault = "";
  categoryDefault = {} as Categories;
  sessionTemplateId = "";
  sessionId = "";
  groupId = "";
  serviceId = "";
  clientId = "";

  async created() {
    window.addEventListener(EVENT_LISTENER, this.onPostMessage);
    parent.postMessage(
      { type: LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES_TYPE.WEBVIEW_READY },
      "*"
    );
    const element = document.querySelector(
      CHAT_BOT_CONSTANTS.DF_MESSENGER.ELEMENTS.BASE
    );
    if (element) element.remove();
  }

  async onPostMessage(event: PostMessageIntelliticks) {
    if (!event || !event.data || !event.data.msgId || !event.data.body) return;
    const data = JSON.parse(event.data.body);
    this.msgIdView = event.data.msgId;
    this.userId = data.id_paciente;
    this.sessionTemplateId = data.sessionTemplateId;
    this.sessionId = data.sessionId ? data.sessionId : "";
    this.groupId = data.groupId ? data.groupId : "";
    this.serviceId = data.serviceId ? data.serviceId : "";
    if (data.userLogged) {
      const auth = await firebase.auth().signInWithCustomToken(data.basicToken);
      if (data.token) {
        localStorage.setItem("TOKEN", data.token);
      } else {
        localStorage.setItem("TOKEN", (await auth.user?.getIdToken()) || "");
      }
      localStorage.setItem(LOCAL_STORAGE.CLIENT_ID, data.clientId);
    }
    await this.loadCategories(data.category, data.subCategory);
    this.showComponent = true;
  }

  closeFrame(text: string, data: string) {
    parent.postMessage(
      {
        type: LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES_TYPE.WEBVIEW,
        jsonData: JSON.stringify(data || []),
        msgId: this.msgIdView,
        text: text
      },
      "*"
    );
  }

  async loadCategories(category: string, subCategory: string) {
    const filterQuery: ListQuery = {};
    filterQuery.isCategoryList = [LIST_QUERY.ACTIVO];
    filterQuery.listType = [LIST_QUERY.PACIENTE];
    filterQuery.isDocumentCategoryList = [LIST_QUERY.ACTIVO];

    const responseData = await this.listService.getChart(filterQuery);
    if (responseData.code === Http.Found && responseData.data) {
      this.categories = responseData.data.listArray.map((item: List) => {
        return {
          idReference: item.id as string,
          name: item.name as string,
          subcategories: item.options.map((option: Option) => ({
            key: option.id,
            name: option.value
          }))
        };
      });
      const categorySearch = this.categories.find(
        (list: Categories) => list.name == category
      );
      if (categorySearch) {
        this.categoryDefault = categorySearch;
        const subCategorySearch = this.categoryDefault.subcategories.find(
          (sub: { key: string; name: string }) => sub.name == subCategory
        );
        this.subCategoryDefault = subCategorySearch
          ? (subCategorySearch as any).name
          : "";
      }
    }
  }

  onCloseUploadDialog() {
    this.closeFrame(
      LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES.CANCEL_SEND_DOCUMENT,
      ""
    );
  }

  async uploadDocument(
    file: File,
    fileName: string,
    fileType: string,
    category: ListCategory,
    subCategory: string
  ) {
    this.showComponent = false;
    const documentId = await this.createDocumentRecord(
      fileName,
      fileType,
      category,
      subCategory
    );
    if (documentId) {
      await this.uploadDocumentToCloud(documentId, fileType, file);
      this.closeFrame(
        LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES.SEND_DOCUMENT_SUCCESSFUL,
        ""
      );
    } else {
      this.closeFrame(
        LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES.SEND_DOCUMENT_ERROR,
        ""
      );
    }
  }

  async createDocumentRecord(
    fileName: string,
    fileType: string,
    category: ListCategory,
    subCategory: string
  ) {
    try {
      const body: DocumentSaveDTO = {
        categoryId: category.idReference,
        category: category.name,
        fileName: `${fileName}.${fileType}`,
        type: fileType,
        subCategory: subCategory,
        userId: this.userId
      };
      const response = await this.documentService.createDocumentRecord(body);
      if (response.code === Http.Ok) {
        return response.data;
      }
    } catch (error) {
      return undefined;
    }
  }

  async uploadDocumentToCloud(
    documentId: string,
    fileType: string,
    file: File
  ) {
    try {
      const storageRef = firebase.storage().ref();
      const response = await storageRef
        .child(
          `${Utils.getClientStoragePath()}/${FILE.FILE_STORAGE_FOLDER}/${
            this.userId
          }/${documentId}.${fileType}`
        )
        .put(file);

      if (response) {
        const filePath = (await response.ref.getDownloadURL()).toString();
        const updateResponse = await this.updateDocumentPath(
          documentId,
          filePath
        );

        if (updateResponse.code != Http.Ok) {
          await this.deleteFileFromStorage(documentId);
          await this.documentService.deleteFailedDocument(documentId);
        } else {
          this.updateSession(documentId);
        }
      } else {
        await this.documentService.deleteFailedDocument(documentId);
      }
    } catch (error) {
      this.closeFrame(
        LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES.LOAD_DOCUMENT_ERROR,
        ""
      );
      return;
    }
  }

  async deleteFileFromStorage(documentId: string) {
    const storageRef = firebase
      .storage()
      .ref(
        `${Utils.getClientStoragePath()}/${FILE.FILE_STORAGE_FOLDER}/${
          this.userId
        }/${documentId}`
      );
    await storageRef.delete();
  }

  async updateDocumentPath(
    documentId: string,
    filePath: string
  ): Promise<Response<void>> {
    const documentUpdateDTO: DocumentUpdateDTO = {
      documentId: documentId,
      storagePath: filePath
    };
    return await this.documentService.updateDocumentPath(documentUpdateDTO);
  }

  async updateSession(documentId: string) {
    try {
      const session: SessionIntelliticks = {
        documents: [documentId],
        serviceId: this.serviceId,
        sessionId: this.sessionId,
        groupId: this.groupId,
        sessionTemplateId: this.sessionTemplateId
      };
      await this.sessionService.updateSession(this.userId, session);
    } catch (error) {
      this.closeFrame(
        LOAD_DOCUMENT_CONSTANT.PARENT_MESAGGES.LOAD_DOCUMENT_ERROR,
        ""
      );
      return;
    }
  }
}
